import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, Users, Key, Lock, AlertTriangle, Settings, Plus, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: Date;
  permissions: string[];
}

interface SecurityPolicy {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

export const EnterpriseAuthentication: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([
    {
      id: '1',
      email: 'admin@company.com',
      name: 'John Admin',
      role: 'Administrator',
      status: 'active',
      lastLogin: new Date(),
      permissions: ['read', 'write', 'delete', 'admin']
    },
    {
      id: '2',
      email: 'processor@company.com',
      name: 'Jane Processor',
      role: 'Processor',
      status: 'active',
      lastLogin: new Date(Date.now() - 24 * 60 * 60 * 1000),
      permissions: ['read', 'write']
    },
    {
      id: '3',
      email: 'viewer@company.com',
      name: 'Bob Viewer',
      role: 'Viewer',
      status: 'inactive',
      lastLogin: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      permissions: ['read']
    }
  ]);

  const [securityPolicies, setSecurityPolicies] = useState<SecurityPolicy[]>([
    {
      id: '1',
      name: 'Multi-Factor Authentication',
      enabled: true,
      description: 'Require MFA for all users',
      severity: 'high'
    },
    {
      id: '2',
      name: 'Password Complexity',
      enabled: true,
      description: 'Enforce strong password requirements',
      severity: 'high'
    },
    {
      id: '3',
      name: 'Session Timeout',
      enabled: false,
      description: 'Auto-logout after 30 minutes of inactivity',
      severity: 'medium'
    },
    {
      id: '4',
      name: 'IP Whitelist',
      enabled: false,
      description: 'Restrict access to specific IP addresses',
      severity: 'low'
    }
  ]);

  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    role: 'Viewer'
  });

  const roles = [
    { id: 'admin', name: 'Administrator', permissions: ['read', 'write', 'delete', 'admin'] },
    { id: 'processor', name: 'Processor', permissions: ['read', 'write'] },
    { id: 'viewer', name: 'Viewer', permissions: ['read'] }
  ];

  const addUser = () => {
    if (newUser.email && newUser.name) {
      const user: User = {
        id: (users.length + 1).toString(),
        ...newUser,
        status: 'active',
        lastLogin: new Date(),
        permissions: roles.find(r => r.name === newUser.role)?.permissions || ['read']
      };
      setUsers([...users, user]);
      setNewUser({ email: '', name: '', role: 'Viewer' });
    }
  };

  const toggleUserStatus = (userId: string) => {
    setUsers(users.map(user => 
      user.id === userId 
        ? { ...user, status: user.status === 'active' ? 'inactive' : 'active' }
        : user
    ));
  };

  const togglePolicy = (policyId: string) => {
    setSecurityPolicies(policies => 
      policies.map(policy => 
        policy.id === policyId 
          ? { ...policy, enabled: !policy.enabled }
          : policy
      )
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      default: return 'secondary';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const securityStats = {
    totalUsers: users.length,
    activeUsers: users.filter(u => u.status === 'active').length,
    enabledPolicies: securityPolicies.filter(p => p.enabled).length,
    lastSecurityEvent: 'No recent events'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Enterprise Authentication & Authorization</h2>
          <p className="text-muted-foreground">Manage users, roles, and security policies</p>
        </div>
        <Button className="gradient-primary">
          <Shield className="w-4 h-4 mr-2" />
          Security Audit
        </Button>
      </div>

      {/* Security Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{securityStats.totalUsers}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Active Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{securityStats.activeUsers}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Security Policies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{securityStats.enabledPolicies}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">Security Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Badge variant="default" className="text-xs">Secure</Badge>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
          <TabsTrigger value="policies">Security Policies</TabsTrigger>
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Add New User</span>
                <Plus className="w-5 h-5" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="user@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.name}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={addUser} className="w-full">
                    Add User
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 border border-border rounded">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground">{user.name}</div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                        <div className="text-xs text-muted-foreground">
                          Last login: {user.lastLogin.toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={getStatusColor(user.status) as any}>
                        {user.status}
                      </Badge>
                      <Badge variant="outline">{user.role}</Badge>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleUserStatus(user.id)}
                        >
                          {user.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button variant="outline" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roles" className="space-y-6">
          {roles.map((role) => (
            <Card key={role.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  {role.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {role.permissions.map((permission) => (
                      <Badge key={permission} variant="secondary">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {users.filter(u => u.role === role.name).length} users assigned
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="policies" className="space-y-6">
          {securityPolicies.map((policy) => (
            <Card key={policy.id}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Lock className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-foreground">{policy.name}</div>
                        <div className="text-sm text-muted-foreground">{policy.description}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={getSeverityColor(policy.severity) as any}>
                      {policy.severity}
                    </Badge>
                    <Switch
                      checked={policy.enabled}
                      onCheckedChange={() => togglePolicy(policy.id)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="audit" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Security Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { action: 'User Login', user: 'john@company.com', time: '2 minutes ago', status: 'success' },
                  { action: 'Password Changed', user: 'jane@company.com', time: '1 hour ago', status: 'success' },
                  { action: 'Failed Login Attempt', user: 'unknown@external.com', time: '3 hours ago', status: 'warning' },
                  { action: 'User Role Updated', user: 'bob@company.com', time: '1 day ago', status: 'info' },
                  { action: 'Security Policy Changed', user: 'admin@company.com', time: '2 days ago', status: 'info' }
                ].map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-border rounded">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        log.status === 'success' ? 'bg-success' :
                        log.status === 'warning' ? 'bg-warning' :
                        'bg-primary'
                      }`} />
                      <div>
                        <div className="font-medium text-foreground">{log.action}</div>
                        <div className="text-sm text-muted-foreground">User: {log.user}</div>
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground">{log.time}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};